import { useCreateCompanyMutationHook } from "@/hooks/mutations";
import { TCreateCompanySchema, CreateCompanySchema, CreateCompanyDefaultValues } from "@/models";
import { onInvalid } from "@/utils/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, SubmitHandler } from "react-hook-form";
import Address from "./FormGroups/Address";
import AssetDocumentary from "./FormGroups/AssetDocumentary";
import AssetFinancingPool from "./FormGroups/AssetFinancingPool";
import BankDetails from "./FormGroups/BankDetails";
import GeneralInformation from "./FormGroups/GeneralInformation";
import InvoiceDocumentary from "./FormGroups/InvoiceDocumentary";
import WebPresence from "./FormGroups/WebPresence";
import { CompanyType } from "@/graphql/__generated__/graphql-operations";
import CompanyDetails from "./FormGroups/CompanyDetails";
import { useEffect } from "react";
import { FramerButton } from "@/components/shared";
import { createFinancierMockData, createServiceProviderMockData } from "./mockData";

function CreateCompany() {

  // Init form
  const {
    handleSubmit,
    register,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<TCreateCompanySchema>({
    resolver: zodResolver(CreateCompanySchema as any),
    defaultValues: CreateCompanyDefaultValues,
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const { createCompanyMutation } = useCreateCompanyMutationHook(reset);

  // Form constants
  const companyType = watch("companytype");
  const isDevLike = ["dev", "development", "local"].includes(import.meta.env.MODE);

  // Reset `serviceproviderinput` when companytype changes to Financier
  useEffect(() => {
    if(companyType === CompanyType.Financier) {
      setValue("serviceproviderinput", undefined);
    }
  }, [companyType]); // watch for changes in companyType

  /**
   * OnSubmit handler for create company form
   * @param {TCreateCompanySchema} data
   */
  const onSubmit: SubmitHandler<TCreateCompanySchema> = (data: TCreateCompanySchema) => {
    // Destructure data
    const { serviceproviderinput, ...restData } = data;
    try {
      const companyInput = companyType === CompanyType.Financier ? restData : { ...restData, serviceproviderinput };
      createCompanyMutation({
        variables: {
          companyInput
        }
      });
    }
    catch (error) {
      return console.log("Error creating company", error);
    }
  };

  const handleFillFinancier = () => {
    reset(createFinancierMockData());
  };

  const handleFillServiceProvider = () => {
    reset(createServiceProviderMockData());
  };

  // Share props
  const formGroupProps = {
    register,
    errors,
    watch,
    setValue
  };

  return (
    <div className="mb-[8rem]">
      <form
        id="create-company-form"
        onSubmit={handleSubmit(onSubmit, onInvalid)}
      >
        {/* Form fields container */}
        <div className="mb-4 mx-4 md:mx-0 mt-6 w-full grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-4">
          {/* Both Financier and SP */}
          <>
            {/* General information form */}
            <GeneralInformation {...formGroupProps} />
            {/* Address */}
            <Address {...formGroupProps} />
            {/* Bank Details */}
            <BankDetails {...formGroupProps} />
          </>

          {/* SP Only */}
          {
            companyType === CompanyType.Serviceprovider && (
              <>
                {/* Company Details */}
                <CompanyDetails {...formGroupProps} />
                {/* Web Presence */}
                <WebPresence {...formGroupProps} />
                {/* Asset Financing Pool */}
                <AssetFinancingPool {...formGroupProps} />
                {/* Asset Documentary */}
                <AssetDocumentary {...formGroupProps} />
                {/* Invoice Documentary */}
                <InvoiceDocumentary {...formGroupProps} />
              </>
            )
          }
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          {isDevLike && (
            <div className="flex flex-wrap gap-2">
              <FramerButton
                type="button"
                onClick={handleFillServiceProvider}
                className="bg-mfpurple text-white hover:bg-mfpurple/80"
              >
                Mock SP Data
              </FramerButton>
              <FramerButton
                type="button"
                onClick={handleFillFinancier}
                className="bg-mfpurple text-white hover:bg-mfpurple/80"
              >
                Mock Financier Data
              </FramerButton>
            </div>
          )}
          <FramerButton
            type="submit"
            className="bg-mforange text-white hover:bg-mforange/80"
          >
            Submit
          </FramerButton>
        </div>
      </form>
    </div>
  );
}

export default CreateCompany;
